import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Tag,
  Space,
  message,
  Modal,
  Spin,
  Alert,
  Divider,
  Statistic,
  Drawer,
  Tooltip,
} from 'antd';
import {
  CreditCardOutlined,
  BarcodeOutlined,
  QrcodeOutlined,
  DeleteOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  SyncOutlined,
  ReloadOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import { useClientSubscriptions, useCancelSubscription, useSyncPaymentStatus, useSyncAsaasPayments } from '../hooks/useClientSubscriptions';
import { useAuthStore } from '@/features/auth/store/authStore';
import { supabase } from '@/shared/lib/supabase';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { useNavigate } from 'react-router-dom';
dayjs.locale('pt-br');

const { Title, Paragraph, Text } = Typography;

interface SubscriptionItemProps {
  subscription: any;
  onCancel: (subscription: any) => void;
  onViewDetails: (subscription: any) => void;
}

const buildSupportUrl = (subscription: any) => {
  const baseUrl = `/client/support?topic=cancelamento&subscription=${subscription.id}`;
  // Se a assinatura tem client_product_id, incluir no URL
  if (subscription.client_product_id) {
    return `${baseUrl}&workflow=${subscription.client_product_id}`;
  }
  return baseUrl;
};

const getCancelBlockedReason = (subscription: any) => {
  if (!subscription) return null;
  const { status, cancelability } = subscription;

  if (status === 'cancelled') {
    return 'Assinatura já cancelada.';
  }

  if (!cancelability) {
    return null;
  }

  if (cancelability.asaas_error) {
    return 'Não foi possível validar os pagamentos automaticamente. Tente novamente ou abra um chamado.';
  }

  if (!cancelability.window_open) {
    return 'Período de cancelamento automático expirou — contate o suporte para prosseguir.';
  }

  if (cancelability.has_pending_payments) {
    return 'Há pagamentos pendentes no Asaas. Regularize-os ou abra um chamado para cancelar.';
  }

  return null;
};

const SubscriptionItem: React.FC<SubscriptionItemProps> = ({
  subscription,
  onViewDetails,
}) => {
  const navigate = useNavigate();
  const { product, billing_type, status, payment_status, next_due_date, created_at, next_open_due_date } = subscription;

  const formatCurrency = (value?: number | null) => {
    if (!value) return 'R$ 0,00';
    return Number(value).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const getBillingIcon = () => {
    switch (billing_type) {
      case 'BOLETO':
        return <BarcodeOutlined style={{ fontSize: 20, color: '#fa8c16' }} />;
      case 'PIX':
        return <QrcodeOutlined style={{ fontSize: 20, color: '#1890ff' }} />;
      case 'CREDIT_CARD':
        return <CreditCardOutlined style={{ fontSize: 20, color: '#52c41a' }} />;
      default:
        return <DollarOutlined style={{ fontSize: 20 }} />;
    }
  };

  const getPaymentStatusColor = () => {
    if (status === 'cancelled') return 'default';
    switch (payment_status) {
      case 'free': return 'success';
      case 'paid': return 'success';
      case 'succeeded': return 'success';
      case 'overdue': return 'error';
      case 'pending': return 'error';
      default: return 'warning';
    }
  };

  const getPaymentStatusLabel = () => {
    if (status === 'cancelled') return 'Cancelada';
    switch (payment_status) {
      case 'free': return 'Gratuito';
      case 'paid': return 'Pago';
      case 'succeeded': return 'Pago';
      case 'overdue': return 'Vencido';
      case 'pending': return 'Pagamento Pendente';
      default: return 'Processando';
    }
  };

  const isFreeProduct = (payment_status === 'free') || (!product?.monthly_price && !product?.setup_fee);
  const supportUrl = buildSupportUrl(subscription);
  const handleOpenSupport = () => navigate(supportUrl);

  // Mensagem de status simplificada
  const getStatusMessage = () => {
    if (status === 'cancelled') return null;
    if (payment_status === 'overdue') {
      return { type: 'error', message: 'Pagamento vencido — regularize para continuar.', showSupport: true };
    }
    // Produto gratuito não tem período de teste
    if (!isFreeProduct && subscription.cancelability?.trial_end) {
      const trialEnd = dayjs(subscription.cancelability.trial_end);
      const daysRemaining = trialEnd.diff(dayjs(), 'day');
      if (daysRemaining > 0) {
        return { type: 'info', message: `${daysRemaining} dias restantes de teste`, showSupport: false };
      }
    }
    return null;
  };

  const statusMessage = getStatusMessage();

  return (
    <Card
      size="small"
      style={{
        borderLeft: `4px solid ${
          status === 'active' ? '#52c41a' : status === 'cancelled' ? '#ff4d4f' : '#faad14'
        }`,
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {!isFreeProduct && getBillingIcon()}
            <div>
              <Text strong style={{ display: 'block', fontSize: 14 }}>
                {product?.name || 'Assinatura'}
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {dayjs(created_at).format('DD [de] MMMM [de] YYYY')}
              </Text>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <Tag color={getPaymentStatusColor()} style={{ margin: 0, fontSize: 11 }}>{getPaymentStatusLabel()}</Tag>
          </div>
        </div>

        {/* Status Message */}
        {statusMessage && (
          <Alert
            message={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{statusMessage.message}</span>
                {statusMessage.showSupport && (
                  <Button type="link" size="small" icon={<CustomerServiceOutlined />} onClick={handleOpenSupport}>
                    Suporte
                  </Button>
                )}
              </div>
            }
            type={statusMessage.type as any}
            style={{ padding: '6px 12px' }}
            showIcon={false}
          />
        )}

        {/* Pricing - Simplificado */}
        {!isFreeProduct && (
          <div style={{ background: '#fafafa', padding: 10, borderRadius: 6 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Próximo pagamento</Text>
                <Text strong style={{ fontSize: 14 }}>{formatCurrency(product?.monthly_price)}/mês</Text>
              </Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                  {billing_type === 'BOLETO' ? 'Boleto' : billing_type === 'PIX' ? 'PIX' : billing_type === 'CREDIT_CARD' ? 'Cartão' : ''}
                </Text>
                <Text strong style={{ fontSize: 14 }}>
                  {next_open_due_date
                    ? dayjs(next_open_due_date).format('DD/MM/YYYY')
                    : next_due_date
                      ? dayjs(next_due_date).format('DD/MM/YYYY')
                      : '-'}
                </Text>
              </Col>
            </Row>
          </div>
        )}

        {isFreeProduct && (
          <div style={{ background: '#f6ffed', padding: 8, borderRadius: 6, textAlign: 'center' }}>
            <Text style={{ color: '#52c41a', fontSize: 12 }}>✨ Produto Gratuito</Text>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<CustomerServiceOutlined />}
              onClick={handleOpenSupport}
              style={{ padding: '0 4px' }}
            >
              Suporte
            </Button>
          </Space>
          <Button
            type="text"
            size="small"
            onClick={() => onViewDetails(subscription)}
            style={{ padding: '0 4px' }}
          >
            •••
          </Button>
        </div>
      </Space>
    </Card>
  );
};

export const MySubscriptionsPage: React.FC = () => {
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [detailsSubscription, setDetailsSubscription] = useState<any>(null);
  const [nextPaymentInfo, setNextPaymentInfo] = useState<{ due_date: string; status: string } | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const { user } = useAuthStore();
  const userId = user?.id;
  const navigate = useNavigate();

  // Fetch subscriptions
  const { data: subscriptions = [], isLoading } = useClientSubscriptions(userId);

  // Cancel subscription mutation
  const cancelSubscription = useCancelSubscription();
  
  // Sync payment status mutation
  const syncPaymentStatus = useSyncPaymentStatus();
  
  // Sync Asaas payments mutation
  const syncAsaasPayments = useSyncAsaasPayments();

  // Sincronizar pagamentos do Asaas e buscar próxima cobrança quando abrir o drawer
  useEffect(() => {
    const syncAndFetchNextPayment = async () => {
      if (!detailsSubscription?.id) {
        setNextPaymentInfo(null);
        return;
      }

      try {
        // Buscar o próximo pagamento
        const { data: nextPayment } = await supabase
          .from('payments')
          .select('due_date, status')
          .eq('subscription_id', detailsSubscription.id)
          .in('status', ['pending', 'awaiting_payment'])
          .gte('due_date', dayjs().format('YYYY-MM-DD'))
          .order('due_date', { ascending: true })
          .limit(1)
          .single();

        setNextPaymentInfo(nextPayment);
      } catch (error) {
        console.error('Error fetching next payment:', error);
        setNextPaymentInfo(null);
      }
    };

    syncAndFetchNextPayment();
  }, [detailsSubscription?.id]);

  const formatCurrency = (value?: number | null) => {
    if (!value) return 'R$ 0,00';
    return Number(value).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  // Statistics
  const stats = {
    total: subscriptions.length,
    active: subscriptions.filter((s: any) => s.status === 'active').length,
    totalMonthly: subscriptions
      .filter((s: any) => s.status === 'active')
      .reduce((sum: number, s: any) => sum + (s.product?.monthly_price || 0), 0),
  };

  const handleCancelSubscription = (targetSubscription: any) => {
    if (!targetSubscription) return;

    const productName = targetSubscription.product?.name || 'Assinatura';
    const cancelability = targetSubscription.cancelability;
    const monthly = Number(targetSubscription.product?.monthly_price || 0);
    const setup = Number(targetSubscription.product?.setup_fee || 0);
    const isFreeProduct = targetSubscription.payment_status === 'free' || (monthly + setup) <= 0;
    const supportUrl = buildSupportUrl(targetSubscription);

    const windowAlert = cancelability?.cancel_allowed_until
      ? `Cancelamento automático liberado até ${dayjs(cancelability.cancel_allowed_until).format('DD/MM/YYYY HH:mm')} (horário local).`
      : 'Cancelamento automático liberado nas próximas 24 horas após a contratação.';

    const renderSupportButton = () => (
      <Button type="primary" icon={<CustomerServiceOutlined />} onClick={() => navigate(supportUrl)}>
        Suporte
      </Button>
    );

    Modal.confirm({
      title: 'Cancelar Assinatura',
      content: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Paragraph>
            Tem certeza que deseja cancelar a assinatura de "{productName}"? Esta ação não pode ser desfeita.
          </Paragraph>
          {cancelability?.window_open !== false && (
            <Alert type="info" showIcon message="Prazo para cancelamento automático" description={windowAlert} />
          )}
          {cancelability?.window_open === false && (
            <Alert
              type="warning"
              showIcon
              message="Período de cancelamento expirado"
              description="Entre em contato com o suporte para concluir o cancelamento."
              action={renderSupportButton()}
            />
          )}
          {cancelability?.refund_required && (
            <Alert
              type="warning"
              showIcon
              message="Reembolso manual"
              description={cancelability.refund_message || 'Esta assinatura já possui cobrança confirmada. Tratativas de estorno são feitas pelo suporte.'}
            />
          )}
          {isFreeProduct && (
            <Alert
              message="Atenção: Produto Gratuito"
              description="Após cancelar, aguarde 24 horas para contratar novamente este produto gratuito."
              type="warning"
              showIcon
              icon={<WarningOutlined />}
            />
          )}
          {targetSubscription.product?.trial_days && (
            <Alert
              type="info"
              showIcon
              message={`${targetSubscription.product.trial_days} dias de período de teste`}
              description="Cancelando até 24h após o fim do trial, nenhuma cobrança será gerada."
            />
          )}
        </Space>
      ),
      okText: 'Sim, cancelar',
      okType: 'danger',
      cancelText: 'Não, manter',
      width: 520,
      onOk: async () => {
        try {
          const result = await cancelSubscription.mutateAsync({
            subscriptionId: targetSubscription.id,
            cancelWorkflow: true,
          });

          message.success(result.message || 'Assinatura cancelada com sucesso!');

          if (result.is_free_product && result.warning_message) {
            setTimeout(() => {
              message.warning(result.warning_message, 5);
            }, 500);
          }

          setDetailsDrawerOpen(false);
          setDetailsSubscription(null);
        } catch (error: any) {
          console.error('Error canceling subscription:', error);

          if (error.code === 'CANCEL_WINDOW_EXPIRED' || error.code === 'PENDING_PAYMENTS') {
            Modal.info({
              title: 'Cancelamento não disponível',
              content: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Paragraph>{error.message || 'Não foi possível concluir o cancelamento automaticamente.'}</Paragraph>
                  {renderSupportButton()}
                </Space>
              ),
              okText: 'Entendi',
            });
          }

          message.error(error.message || 'Erro ao cancelar assinatura');
        }
      },
    });
  };

  const handleSyncPaymentStatus = async (subscriptionId?: string) => {
    setIsSyncing(true);
    try {
      const asaasSyncResult = await syncAsaasPayments.mutateAsync();

      const result = await syncPaymentStatus.mutateAsync({
        subscriptionId,
        syncAll: !subscriptionId,
      });

      const updatedCount = result.updated_count || 0;
      const nextDueUpdated = asaasSyncResult?.next_due_updated || 0;
      
      if (updatedCount > 0 || nextDueUpdated > 0) {
        const parts = [];
        if (updatedCount > 0) {
          parts.push(`${updatedCount} ${updatedCount === 1 ? 'assinatura atualizada' : 'assinaturas atualizadas'}`);
        }
        if (nextDueUpdated > 0) {
          parts.push(`${nextDueUpdated} ${nextDueUpdated === 1 ? 'próxima cobrança atualizada' : 'próximas cobranças atualizadas'}`);
        }
        message.success(`${parts.join(' e ')}!`);
      } else {
        message.info('Nenhuma atualização necessária. Status já está sincronizado.');
      }
      
      console.log('Sync result:', result);
    } catch (error: any) {
      console.error('Error syncing payment status:', error);
      message.error(error.message || 'Erro ao sincronizar status de pagamento');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleViewDetails = (subscription: any) => {
    setDetailsSubscription(subscription);
    setDetailsDrawerOpen(true);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={2} style={{ marginBottom: 4, background: 'linear-gradient(135deg, #7c3aed, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Minhas Assinaturas
          </Title>
          <Paragraph type="secondary" style={{ marginTop: 0 }}>
            Gerencie suas assinaturas e pagamentos
          </Paragraph>
        </div>
        <Button
          type="primary"
          icon={isSyncing ? <SyncOutlined spin /> : <ReloadOutlined />}
          loading={isSyncing}
          onClick={() => handleSyncPaymentStatus()}
          disabled={subscriptions.length === 0}
        >
          Atualizar Status
        </Button>
      </div>

      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8}>
          <Card 
            loading={isLoading} 
            size="small"
            style={{ 
              background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(124, 58, 237, 0.05) 100%)',
              borderRadius: 16,
              border: '1px solid rgba(124, 58, 237, 0.2)',
            }}
          >
            <Statistic
              title={<Text type="secondary" style={{ fontSize: 12 }}>TOTAL</Text>}
              value={stats.total}
              prefix={<CreditCardOutlined style={{ color: '#7c3aed' }} />}
              valueStyle={{ color: '#7c3aed' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card 
            loading={isLoading} 
            size="small"
            style={{ 
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
              borderRadius: 16,
              border: '1px solid rgba(16, 185, 129, 0.2)',
            }}
          >
            <Statistic
              title={<Text type="secondary" style={{ fontSize: 12 }}>ATIVAS</Text>}
              value={stats.active}
              valueStyle={{ color: '#10b981' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card 
            loading={isLoading} 
            size="small"
            style={{ 
              background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(249, 115, 22, 0.05) 100%)',
              borderRadius: 16,
              border: '1px solid rgba(249, 115, 22, 0.2)',
            }}
          >
            <Statistic
              title={<Text type="secondary" style={{ fontSize: 12 }}>GASTO MENSAL</Text>}
              value={formatCurrency(stats.totalMonthly)}
              valueStyle={{ color: '#f97316', fontSize: 18 }}
              prefix="$"
            />
          </Card>
        </Col>
      </Row>

      {/* Subscriptions List */}
      {isLoading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <Paragraph style={{ marginTop: 16 }}>Carregando assinaturas...</Paragraph>
          </div>
        </Card>
      ) : subscriptions.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <CreditCardOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
            <Title level={4} type="secondary">
              Você não possui assinaturas ativas
            </Title>
            <Button type="primary" href="/client/marketplace">
              Explorar Marketplace
            </Button>
          </div>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {subscriptions.map((subscription: any) => (
            <Col xs={24} md={12} xl={8} key={subscription.id}>
              <SubscriptionItem
                subscription={subscription}
                onCancel={handleCancelSubscription}
                onViewDetails={handleViewDetails}
              />
            </Col>
          ))}
        </Row>
      )}

      {/* Details Drawer */}
      <Drawer
        title={
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
              {detailsSubscription?.product?.name || 'Assinatura'}
            </div>
          </div>
        }
        open={detailsDrawerOpen}
        width={520}
        onClose={() => {
          setDetailsDrawerOpen(false);
          setDetailsSubscription(null);
        }}
      >
        {detailsSubscription && (() => {
          const isFreeProduct = !detailsSubscription.product?.monthly_price && !detailsSubscription.product?.setup_fee;
          const monthlyPrice = detailsSubscription.product?.monthly_price || 0;
          const setupFee = detailsSubscription.product?.setup_fee || 0;
          const hasRecurring = monthlyPrice > 0;
          
          // Usar próxima cobrança real dos pagamentos ou fallback para next_due_date
          const actualNextDueDate = nextPaymentInfo?.due_date || detailsSubscription.next_due_date;
          const nextPaymentStatus = nextPaymentInfo?.status || detailsSubscription.payment_status;
          
          // Traduzir ciclo
          const getCycleLabel = (cycle: string) => {
            const cycles: Record<string, string> = {
              'MONTHLY': 'Mensal',
              'WEEKLY': 'Semanal',
              'QUARTERLY': 'Trimestral',
              'YEARLY': 'Anual',
            };
            return cycles[cycle] || 'Mensal';
          };

          // Calcular período de teste
          const trialInfo = (() => {
            if (!detailsSubscription.product?.trial_days || detailsSubscription.product.trial_days === 0) {
              return null;
            }
            const trialEnd = dayjs(detailsSubscription.created_at).add(detailsSubscription.product.trial_days, 'day');
            const isTrialActive = dayjs().isBefore(trialEnd);
            const daysRemaining = trialEnd.diff(dayjs(), 'day');
            
            return {
              days: detailsSubscription.product.trial_days,
              endDate: trialEnd,
              isActive: isTrialActive,
              daysRemaining: daysRemaining,
            };
          })();

          return (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* Descrição do Produto */}
              {detailsSubscription.product?.description && (
                <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 8 }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {detailsSubscription.product.description}
                  </Text>
                </div>
              )}

              {/* Período de Teste - Não exibir para produtos gratuitos */}
              {trialInfo && !isFreeProduct && (
                <>
                  {trialInfo.isActive ? (
                    <Alert
                      message={`Período de teste ativo - ${trialInfo.daysRemaining} ${trialInfo.daysRemaining === 1 ? 'dia restante' : 'dias restantes'}`}
                      description={
                        <>
                          Seu teste termina em <strong>{trialInfo.endDate.format('DD/MM/YYYY')}</strong>.
                          {hasRecurring && ' A primeira cobrança será realizada após este período.'}
                        </>
                      }
                      type="info"
                      showIcon
                      style={{ marginBottom: 0 }}
                    />
                  ) : (
                    <div style={{ background: '#fafafa', padding: 10, borderRadius: 6, border: '1px solid #e8e8e8' }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Período de teste de <strong>{trialInfo.days} dias</strong> encerrado em{' '}
                        <strong>{trialInfo.endDate.format('DD/MM/YYYY')}</strong>
                      </Text>
                    </div>
                  )}
                </>
              )}
              {/* Valores - Clean e Compacto */}
              <div>
                <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 12 }}>
                  💰 Valores
                </Text>
                
                {isFreeProduct ? (
                  <div style={{ background: '#f6ffed', padding: 12, borderRadius: 8, textAlign: 'center' }}>
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24, marginBottom: 8, display: 'block' }} />
                    <Text style={{ color: '#52c41a', fontSize: 14, fontWeight: 500 }}>
                      Produto Gratuito
                    </Text>
                  </div>
                ) : (
                  <div style={{ background: '#fafafa', padding: 14, borderRadius: 8 }}>
                    {hasRecurring ? (
                      <>
                        {/* Mensalidade */}
                        <div style={{ marginBottom: 12 }}>
                          <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Valor Mensal</Text>
                          <Text strong style={{ fontSize: 20, color: '#7c3aed' }}>
                            {formatCurrency(monthlyPrice)}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12 }}> / {getCycleLabel(detailsSubscription.cycle || 'MONTHLY').toLowerCase()}</Text>
                        </div>

                        {/* Taxa de Adesão */}
                        {setupFee > 0 && (
                          <div style={{ background: '#fff', padding: 10, borderRadius: 6, border: '1px dashed #d9d9d9' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                              <Text type="secondary" style={{ fontSize: 12 }}>Taxa de Configuração</Text>
                              <Text strong style={{ color: '#ec4899' }}>{formatCurrency(setupFee)}</Text>
                            </div>
                            <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: 1.4 }}>
                              Pago apenas uma vez. Essa taxa cobre o suporte especializado para configuração e ajustes iniciais do produto.
                            </Text>
                          </div>
                        )}

                        {/* Próximo Pagamento */}
                        {detailsSubscription.status === 'active' && actualNextDueDate && (
                          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e8e8e8' }}>
                            <div style={{ marginBottom: 8 }}>
                              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Próxima cobrança</Text>
                              <Text strong style={{ fontSize: 14 }}>{dayjs(actualNextDueDate).format('DD/MM/YYYY')}</Text>
                            </div>
                            <Tag 
                              color={
                                nextPaymentStatus === 'paid' || nextPaymentStatus === 'free'
                                  ? 'success'
                                  : nextPaymentStatus === 'overdue'
                                    ? 'error'
                                    : 'warning'
                              }
                              style={{ margin: 0, fontSize: 11 }}
                            >
                              {nextPaymentStatus === 'paid' || nextPaymentStatus === 'free'
                                ? '✓ Pago'
                                : nextPaymentStatus === 'overdue'
                                  ? 'Vencido'
                                  : 'Aguardando pagamento'}
                            </Tag>
                          </div>
                        )}
                      </>
                    ) : (
                      /* Pagamento Único */
                      <div>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Pagamento Único</Text>
                        <Text strong style={{ fontSize: 20, color: '#52c41a' }}>
                          {formatCurrency(setupFee)}
                        </Text>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Divider style={{ margin: '12px 0' }} />

              {/* Informações de Pagamento */}
              <div>
                <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 12 }}>
                  📋 Informações
                </Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Data de contratação {dayjs(detailsSubscription?.created_at).format('DD/MM/YYYY')}
                  </Text>
                </div>
                <div style={{ background: '#fafafa', padding: 12, borderRadius: 8 }}>
                  <Row gutter={[12, 8]}>
                    {!isFreeProduct && (
                      <Col span={12}>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Forma de Pagamento</Text>
                        <Text style={{ fontSize: 13 }}>
                          {detailsSubscription.billing_type === 'CREDIT_CARD'
                            ? '💳 Cartão'
                            : detailsSubscription.billing_type === 'BOLETO'
                              ? '📄 Boleto'
                              : '📱 PIX'}
                        </Text>
                      </Col>
                    )}
                    <Col span={isFreeProduct ? 24 : 12}>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Status Pagamento</Text>
                      <Tag 
                        color={
                          detailsSubscription.payment_status === 'paid' || detailsSubscription.payment_status === 'free'
                            ? 'success'
                            : detailsSubscription.payment_status === 'overdue'
                              ? 'error'
                              : 'warning'
                        }
                        style={{ margin: 0 }}
                      >
                        {detailsSubscription.payment_status === 'free'
                          ? 'Gratuito'
                          : detailsSubscription.payment_status === 'paid'
                            ? 'Em dia'
                            : detailsSubscription.payment_status === 'overdue'
                              ? 'Vencido'
                              : 'Pendente'}
                      </Tag>
                    </Col>
                    {detailsSubscription.cancelled_at && (
                      <Col span={24}>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Cancelado em</Text>
                        <Text style={{ fontSize: 13, color: '#ff4d4f' }}>
                          {dayjs(detailsSubscription.cancelled_at).format('DD/MM/YYYY HH:mm')}
                        </Text>
                      </Col>
                    )}
                  </Row>
                </div>
              </div>

              {/* Actions */}
              {detailsSubscription.status !== 'cancelled' && (
                <>
                  <Divider style={{ margin: '12px 0' }} />
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    {(() => {
                      const detailBlockedReason = getCancelBlockedReason(detailsSubscription);
                      return (
                        <Tooltip title={detailBlockedReason || undefined}>
                          <Button
                            block
                            danger
                            icon={<DeleteOutlined />}
                            disabled={Boolean(detailBlockedReason)}
                            onClick={() => {
                              setDetailsDrawerOpen(false);
                              handleCancelSubscription(detailsSubscription);
                            }}
                          >
                            Cancelar Assinatura
                          </Button>
                        </Tooltip>
                      );
                    })()}
                    <Button block type="default" href="/client/marketplace">
                      Explorar Marketplace
                    </Button>
                  </Space>
                </>
              )}
            </Space>
          );
        })()}
      </Drawer>
    </div>
  );
};

export default MySubscriptionsPage;
